import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { 
  MessageSquare, 
  FileCheck, 
  Package, 
  Truck,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

export const HowItWorksPage = () => {
  const { t, isRTL } = useLanguage();

  const steps = [
    { 
      icon: MessageSquare, 
      number: '01',
      titleKey: 'howItWorks.step1Title', 
      descKey: 'howItWorks.step1Desc',
      detailsKey: 'howItWorks.step1Details'
    },
    { 
      icon: FileCheck, 
      number: '02',
      titleKey: 'howItWorks.step2Title', 
      descKey: 'howItWorks.step2Desc',
      detailsKey: 'howItWorks.step2Details'
    },
    { 
      icon: Package, 
      number: '03',
      titleKey: 'howItWorks.step3Title', 
      descKey: 'howItWorks.step3Desc',
      detailsKey: 'howItWorks.step3Details'
    },
    { 
      icon: Truck, 
      number: '04',
      titleKey: 'howItWorks.step4Title', 
      descKey: 'howItWorks.step4Desc',
      detailsKey: 'howItWorks.step4Details'
    },
  ];

  const benefitKeys = [
    'howItWorks.benefit1',
    'howItWorks.benefit2',
    'howItWorks.benefit3',
    'howItWorks.benefit4',
    'howItWorks.benefit5',
    'howItWorks.benefit6'
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="how-it-works-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-heading mb-6" data-testid="how-it-works-title">
              {t('howItWorks.title')}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed" data-testid="how-it-works-subtitle">
              {t('howItWorks.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="steps-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Connection Line (Desktop) */}
            <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-slate-200" style={{ width: 'calc(100% - 200px)', margin: '0 100px' }} />
            
            <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 ${isRTL ? 'lg:flex-row-reverse' : ''}`}>
              {steps.map((step, index) => (
                <div 
                  key={index} 
                  className="relative flex flex-col items-center text-center"
                  data-testid={`step-${index + 1}`}
                >
                  {/* Step Number Circle */}
                  <div className="relative z-10 w-20 h-20 bg-white border-4 border-primary-blue rounded-full flex items-center justify-center mb-6 shadow-lg">
                    <span className="text-2xl font-bold text-primary-blue font-heading">{step.number}</span>
                  </div>
                  
                  {/* Icon */}
                  <div className="w-14 h-14 bg-primary-blue/10 rounded-xl flex items-center justify-center mb-4">
                    <step.icon className="w-7 h-7 text-primary-blue" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">
                    {t(step.titleKey)}
                  </h3>
                  <p className="text-sm text-slate-600 mb-3">
                    {t(step.descKey)}
                  </p>
                  <p className="text-sm text-slate-500 leading-relaxed">
                    {t(step.detailsKey)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className={isRTL ? 'order-2 text-right' : 'order-1 text-left'}>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-heading mb-6">
                {t('howItWorks.whyWorkWithUs')}
              </h2>
              <p className="text-base text-slate-600 mb-8 leading-relaxed">
                {t('howItWorks.whyWorkWithUsDesc')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {benefitKeys.map((key, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-accent-red flex-shrink-0" />
                    <span className="text-sm text-slate-700">{t(key)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className={isRTL ? 'order-1' : 'order-2'}>
              <img
                src="https://images.unsplash.com/photo-1632517306067-b54ab4d1f98d?auto=format&fit=crop&w=800&q=80"
                alt="Container shipping"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-primary-blue" data-testid="how-it-works-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white font-heading mb-4">
            {t('howItWorks.readyToStart')}
          </h2>
          <p className="text-lg text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('howItWorks.readyToStartDesc')}
          </p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-white text-primary-blue hover:bg-slate-100 px-8"
              data-testid="how-it-works-contact-btn"
            >
              {t('hero.cta')}
              <ArrowRight className={`w-5 h-5 ${isRTL ? 'me-2 rotate-180' : 'ms-2'}`} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
